import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'

import { ThemeProvider } from './components/ThemeProvider'
import { AuthProvider } from './components/AuthProvider'
import { TimeZoneProvider } from './contexts/TimeZoneContext'
import { DeploymentModeProvider } from './contexts/DeploymentModeContext'
import { ROUTES } from './routes'
import { toRouteObjects } from './lib/routeConfig'

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Built once at module load (react-router's recommendation for a static route
// tree). The tree itself lives in ./routes, where every entry declares its
// guard decision in its type — see ROUTE_PERMISSION in ./lib/routeConfig.
const router = createBrowserRouter(toRouteObjects(ROUTES))

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <DeploymentModeProvider>
        <ThemeProvider defaultTheme="system" storageKey="flostel-ui-theme">
          <AuthProvider>
            <TimeZoneProvider>
              <RouterProvider router={router} />
            </TimeZoneProvider>
          </AuthProvider>

          {/* Toaster + dev tools need no router context → siblings of RouterProvider */}
          <Toaster
            position="top-right"
            richColors
            closeButton
            gap={8}
            toastOptions={{ style: { boxShadow: 'var(--shadow-popover)' } }}
          />
        </ThemeProvider>
      </DeploymentModeProvider>

      {/* React Query Devtools (only in development) */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  )
}

export default App
